/* V218 — Hero copy A/B test admin dashboard.
 *
 * Shows 4 hero question variants with impressions, scan clicks, scan completes,
 * CTR (clicks / impressions) and CVR (completes / impressions). Plus a per-week
 * breakdown and a per-language breakdown for the 6 supported markets.
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function findWinner(rows, metric = "cvr_pct", minImpressions = 100) {
  const eligible = (rows || []).filter((r) => (r.impressions || 0) >= minImpressions);
  if (!eligible.length) return null;
  return eligible.reduce((best, r) => (r[metric] > best[metric] ? r : best), eligible[0]);
}

function VariantRow({ row, winner }) {
  const isWinner = winner && winner.variant === row.variant;
  return (
    <div
      className={`grid grid-cols-12 items-center gap-3 px-4 py-3 border-b border-zinc-900 ${isWinner ? "bg-emerald-500/5" : ""}`}
      data-testid={`hero-ab-row-${row.variant}`}
    >
      <div className="col-span-12 sm:col-span-4">
        <div className="text-sm font-bold text-white">{row.label}</div>
      </div>
      <div className="col-span-3 sm:col-span-2 text-zinc-300 text-sm font-mono">
        {row.impressions.toLocaleString("sk-SK")}
      </div>
      <div className="col-span-3 sm:col-span-2 text-zinc-300 text-sm font-mono">
        {row.scan_clicks.toLocaleString("sk-SK")}
        <span className="text-[10px] text-zinc-500 ml-1">({row.ctr_pct}%)</span>
      </div>
      <div className="col-span-3 sm:col-span-2 text-zinc-300 text-sm font-mono">
        {row.scan_completes.toLocaleString("sk-SK")}
      </div>
      <div className="col-span-3 sm:col-span-2 flex items-center gap-2">
        <div className={`text-base font-black ${isWinner ? "text-emerald-300" : "text-white"}`}>
          {row.cvr_pct}%
        </div>
        {isWinner && (
          <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-300 bg-emerald-500/15 border border-emerald-500/30 rounded-full px-2 py-0.5">
            winner
          </span>
        )}
      </div>
    </div>
  );
}

function VariantTableHeader() {
  return (
    <div className="grid grid-cols-12 items-center gap-3 px-4 py-2 bg-zinc-900 text-[10px] text-zinc-500 font-mono uppercase tracking-wider">
      <div className="col-span-12 sm:col-span-4">Variant</div>
      <div className="col-span-3 sm:col-span-2">Impressions</div>
      <div className="col-span-3 sm:col-span-2">Scan kliky · CTR</div>
      <div className="col-span-3 sm:col-span-2">Dokončené</div>
      <div className="col-span-3 sm:col-span-2">CVR (cmp/imp)</div>
    </div>
  );
}

export default function AdminHeroABTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    const token = localStorage.getItem("admin_token") || "";
    if (!token) {
      setErr("Admin token chýba.");
      setLoading(false);
      return;
    }
    setLoading(true);
    axios
      .get(`${API}/hero-ab/admin/stats`, { headers: { "X-Admin-Token": token } })
      .then((r) => {
        setData(r.data);
        setErr("");
      })
      .catch((e) => setErr(e.response?.data?.detail || "load_failed"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  if (err) {
    return (
      <div className="p-6 text-rose-300" data-testid="hero-ab-error">
        {err}
      </div>
    );
  }
  if (loading || !data) {
    return (
      <div className="p-6 text-zinc-500" data-testid="hero-ab-loading">
        Načítavam A/B dáta…
      </div>
    );
  }

  const lifetimeWinner = findWinner(data.lifetime || []);
  const lifetimeCTRWinner = findWinner(data.lifetime || [], "ctr_pct");

  return (
    <div className="p-3 sm:p-6 space-y-6" data-testid="admin-hero-ab-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl sm:text-2xl font-black text-white">🧪 Hero copy A/B test</h2>
          <p className="text-zinc-400 text-sm mt-1">
            V218 — 4 varianty hero otázky · 6 jazykov · sticky 30 dní · current week:{" "}
            <span className="font-mono text-emerald-300">{data.current_week}</span>
          </p>
        </div>
        <button
          data-testid="hero-ab-refresh"
          onClick={load}
          className="px-3 py-1.5 bg-zinc-900 border border-zinc-700 text-zinc-300 text-xs rounded hover:bg-zinc-800"
        >
          ↻ Obnoviť
        </button>
      </div>

      {/* Lifetime */}
      <div className="bg-zinc-950/60 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between flex-wrap gap-2">
          <h3 className="text-sm font-bold text-zinc-200">Lifetime totals (všetky jazyky)</h3>
          <div className="flex gap-2 text-xs font-mono">
            {lifetimeCTRWinner && (
              <span className="text-amber-300">
                CTR víťaz: {lifetimeCTRWinner.variant} ({lifetimeCTRWinner.ctr_pct}%)
              </span>
            )}
            {lifetimeWinner && (
              <span className="text-emerald-300">
                CVR víťaz: {lifetimeWinner.variant} ({lifetimeWinner.cvr_pct}%)
              </span>
            )}
          </div>
        </div>
        <VariantTableHeader />
        {(data.lifetime || []).map((row) => (
          <VariantRow key={row.variant} row={row} winner={lifetimeWinner} />
        ))}
      </div>

      {/* Per language */}
      {(data.languages || []).length > 0 && (
        <div className="space-y-4">
          <h3 className="text-sm font-bold text-zinc-200">Podľa jazyka</h3>
          {(data.languages || []).map((lng) => {
            const w = findWinner(lng.variants);
            return (
              <div
                key={lng.language}
                className="bg-zinc-950/60 border border-zinc-800 rounded-xl overflow-hidden"
                data-testid={`hero-ab-lang-${lng.language}`}
              >
                <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
                  <h4 className="text-sm font-bold text-zinc-300 uppercase">{lng.language}</h4>
                  {w && (
                    <span className="text-xs text-emerald-300 font-mono">
                      Víťaz: {w.variant}
                    </span>
                  )}
                </div>
                <VariantTableHeader />
                {lng.variants.map((row) => (
                  <VariantRow key={row.variant} row={row} winner={w} />
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Per week */}
      {(data.weeks || []).length > 0 && (
        <div className="space-y-4">
          <h3 className="text-sm font-bold text-zinc-200">Podľa týždňa</h3>
          {(data.weeks || []).map((wk) => {
            const w = findWinner(wk.variants);
            return (
              <div
                key={wk.week}
                className="bg-zinc-950/60 border border-zinc-800 rounded-xl overflow-hidden"
              >
                <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
                  <h4 className="text-sm font-bold text-zinc-300">Týždeň {wk.week}</h4>
                  {w && (
                    <span className="text-xs text-emerald-300 font-mono">
                      Víťaz: {w.variant}
                    </span>
                  )}
                </div>
                <VariantTableHeader />
                {wk.variants.map((row) => (
                  <VariantRow key={row.variant} row={row} winner={w} />
                ))}
              </div>
            );
          })}
        </div>
      )}

      {(data.lifetime || []).every((r) => r.impressions === 0) && (
        <div className="text-zinc-500 text-sm border border-dashed border-zinc-800 rounded-lg p-4">
          Žiadne impresie zatiaľ. Po otvorení homepage uvidíš prvé eventy do pár sekúnd.
        </div>
      )}
    </div>
  );
}
